/**
 * `BrandTokens` — the themeable palette, live, inside the gallery.
 *
 * The `/components` gallery is the owner-declared source of truth for
 * UI/UX ("recuerda SIEMPRE actualizar los elementos de UI UX en
 * /components. Esos son la fuente de la verdad" — 2026-07-14, recorded as
 * rule 13 in this package's CLAUDE.md). Plan C fase 3 makes colour a
 * per-tenant, runtime thing, so the gallery has to be able to answer two
 * questions at a glance:
 *
 *   1. WHICH tokens follow a customer's theme, and what are they RIGHT
 *      NOW on this page? Every swatch below reads its colour through the
 *      same `--bi-*` custom property the rest of the product uses, so
 *      loading a themed builder host repaints this panel and every widget
 *      underneath it together. If they ever disagree, the panel is wrong
 *      in the same way the product is, which is the point.
 *   2. WHICH tokens do NOT, and why. The band pair, the warning amber and
 *      the chart-chrome neutrals are rendered in a second group that says
 *      "fixed" in words — not greyed out, not hidden. The owner is
 *      red-green colourblind and those tones exist because of it; a
 *      catalogue that quietly omitted them would make the constraint
 *      invisible to the next person.
 *
 * No props, no data: it is a live readout of the document it is rendered
 * in.
 */
import { brand } from '../brand.js';
import { cssBrandColor } from './theme.js';

const THEMEABLE = [
  ['--bi-accent', 'accent · brand colour', cssBrandColor('accent')],
  ['--bi-accent-600', 'accent dark', cssBrandColor('accent', 600)],
  ['--bi-primary', 'primary (brand gradient start)', cssBrandColor('primary')],
  ['--bi-sidebar-from', 'sidebar gradient · from', cssBrandColor('sidebar', 'from')],
  ['--bi-sidebar-to', 'sidebar gradient · to', cssBrandColor('sidebar', 'to')],
];

const FIXED = [
  ['band positive', brand.semantic.bandPositive, 'teal — survives deuteranopia'],
  ['band negative', brand.semantic.bandNegative, 'deep red — differs in lightness too'],
  ['warning', brand.semantic.warning, 'amber — the caution band'],
  ['chart text', brand.neutral.text, 'axis + label ink'],
  ['chart faint', brand.neutral.faint, 'tick labels'],
  ['chart grid', brand.neutral.grid, 'gridlines'],
];

function Swatch({ color, label, note, code }) {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <span
        className="inline-block h-7 w-7 shrink-0 rounded-md border border-neutral-300"
        style={{ background: color }}
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span className="block truncate text-[12px] font-semibold text-neutral-800">
          {label}
        </span>
        <span className="block truncate text-xs text-neutral-500">
          {code || note}
        </span>
      </span>
    </div>
  );
}

export function BrandTokens({ height = 340 }) {
  return (
    <div className="w-full overflow-auto" style={{ maxHeight: height }}>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-neutral-700">
            Follows the customer&apos;s theme
          </h4>
          <p className="mb-2 text-xs leading-snug text-neutral-500">
            Each swatch paints itself through its CSS custom property, exactly
            like every widget below. A theme saved in the platform console
            changes these on the next page load — no rebuild.
          </p>
          {THEMEABLE.map(([varName, label, color]) => (
            <Swatch key={varName} color={color} label={label} code={varName} />
          ))}
        </div>
        <div>
          <h4 className="mb-1 text-xs font-bold uppercase tracking-wider text-neutral-700">
            Fixed — deliberately not editable
          </h4>
          <p className="mb-2 text-xs leading-snug text-neutral-500">
            Status bands, the warning amber and chart chrome. The product owner
            is red-green colourblind: the bands avoid the green/red pair a
            deuteranope cannot separate, and always carry a ✓ / ! / × glyph so
            state never rides on colour alone. These have no custom property, so
            there is nowhere for a tenant override to be written.
          </p>
          {FIXED.map(([label, color, note]) => (
            <Swatch key={label} color={color} label={label} note={`${color} — ${note}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
